/// Safety equipment + first-aid material — the syllabus content that
/// sits behind Day Skipper, Coastal Skipper, Yachtmaster Offshore +
/// Ocean and the RYA First Aid course.
///
/// Six domains:
///
///   1. Flares — types, when to use, packs by distance category,
///      firing safety. Specifically requested in COMING_SOON.md.
///   2. First-aid kits — Cat A/B/C/D + distance categories + what
///      makes Cat C different from a standard kit. Also requested.
///   3. Liferaft — types, service intervals, launch + boarding
///      sequence, grab-bag contents.
///   4. EPIRB + PLB — registration, hydrostatic release, activation.
///   5. Lifejackets — manual / automatic / hydrostatic + service.
///   6. First aid for sea — cold-water shock + hypothermia +
///      seasickness + casualty assessment.
///
/// Pure data; UI lives in apps/web.

// ───────────────────────────────────────────────────────────────────
// 1. Flares
// ───────────────────────────────────────────────────────────────────

export type FlareKind =
  | "parachute-red"
  | "handheld-red"
  | "smoke-orange"
  | "handheld-white"
  | "led-electronic";

export interface Flare {
  id: FlareKind;
  name: string;
  /// One-line summary.
  summary: string;
  /// Approximate visible range (or "N/A" for non-distress).
  range: string;
  /// What you use it for, on what part of the rescue timeline.
  use: string;
  /// True if a recognised SOLAS distress signal.
  isDistress: boolean;
  /// Operational priority — when in a distress to fire it.
  priority: "alert" | "guide-in" | "day-signalling" | "warning" | "alternative";
}

export const FLARES: readonly Flare[] = Object.freeze([
  {
    id: "parachute-red",
    name: "Red parachute rocket",
    summary:
      "Rocket-launched red flare that climbs to ~300 m and descends slowly on a parachute, burning for ~40 seconds.",
    range: "30+ nautical miles at altitude",
    use:
      "FIRST flare to fire once you know SAR is coordinated — visible at huge range, gives the searchers a bearing toward you. Fire at ~75° elevation INTO wind so the parachute drifts downwind over the water.",
    isDistress: true,
    priority: "alert",
  },
  {
    id: "handheld-red",
    name: "Handheld red flare",
    summary:
      "Hand-held flare burning bright red for ~60 seconds. Visible at medium range.",
    range: "5–7 nautical miles by day, further by night",
    use:
      "GUIDE-IN flare — fire when SAR / lifeboat / helicopter is closing on your position and you want to pinpoint yourself. Hold over the leeward side, gloves on, face turned away.",
    isDistress: true,
    priority: "guide-in",
  },
  {
    id: "smoke-orange",
    name: "Orange handheld / floating smoke",
    summary:
      "Hand-held or floating canister producing dense orange smoke for ~3 minutes.",
    range: "3 nautical miles by day",
    use:
      "DAYTIME alternative to a red handheld — much more visible than a flare against a bright sky. Hold over the leeward side or drop floating canister into the water alongside.",
    isDistress: true,
    priority: "day-signalling",
  },
  {
    id: "handheld-white",
    name: "Handheld white flare",
    summary:
      "Hand-held white flare burning intensely white for ~60 seconds.",
    range: "Not a distress signal — collision-avoidance only.",
    use:
      "ILLUMINATE the sails / hull to make yourself visible to an approaching vessel that hasn't seen you. NEVER used as a distress signal — fire one and a passing skipper will think you're fine.",
    isDistress: false,
    priority: "warning",
  },
  {
    id: "led-electronic",
    name: "LED electronic flare",
    summary:
      "MCA-approved electronic visual distress signal — high-intensity LED unit, lifetime use, no expiry.",
    range: "Up to 10 nm by night; less effective by day.",
    use:
      "ALTERNATIVE to a pyrotechnic handheld red. Always-ready, safer to handle, no disposal issue. Recognised as a distress signal under recent MCA guidance, but recommended IN ADDITION to pyrotechnics rather than as a full replacement.",
    isDistress: true,
    priority: "alternative",
  },
]);

export type FlarePackArea =
  | "inshore"
  | "coastal"
  | "offshore"
  | "ocean";

export interface FlarePack {
  area: FlarePackArea;
  /// Distance from a safe haven the pack covers.
  range: string;
  /// Itemised contents.
  contents: ReadonlyArray<{ flare: FlareKind; quantity: number }>;
  /// Operational notes.
  note: string;
}

export const FLARE_PACKS: readonly FlarePack[] = Object.freeze([
  {
    area: "inshore",
    range: "Within sight of shore / harbour limits.",
    contents: [
      { flare: "handheld-red", quantity: 2 },
      { flare: "smoke-orange", quantity: 2 },
    ],
    note: "RYA-recommended minimum pack for day sailing on inland or sheltered waters. EPIRB + VHF + mobile phone usually closer-range alternatives in this area.",
  },
  {
    area: "coastal",
    range: "Up to ~7 nm from a safe haven.",
    contents: [
      { flare: "parachute-red", quantity: 2 },
      { flare: "handheld-red", quantity: 2 },
      { flare: "smoke-orange", quantity: 2 },
    ],
    note: "Standard Day Skipper / Coastal coastal pack — 2 of each common type. Add LED flare as a daily-use alternative if available.",
  },
  {
    area: "offshore",
    range: "From ~7 nm out to ~60 nm.",
    contents: [
      { flare: "parachute-red", quantity: 4 },
      { flare: "handheld-red", quantity: 4 },
      { flare: "smoke-orange", quantity: 2 },
    ],
    note: "RYA Cruising Conference recommendation. EPIRB primary, flares secondary at this range; double the parachute count for offshore racing.",
  },
  {
    area: "ocean",
    range: "Blue-water passages > 60 nm or beyond VHF range.",
    contents: [
      { flare: "parachute-red", quantity: 6 },
      { flare: "handheld-red", quantity: 6 },
      { flare: "smoke-orange", quantity: 4 },
    ],
    note: "SOLAS-style pack. EPIRB + satphone + flares form the alert triad; flares are pinpoint signals once SAR has reached you, not primary alert.",
  },
]);

/// Firing-safety checklist common to all pyrotechnic flares. Used by
/// the Reference panel and by the &quot;which flare?&quot; game's
/// reveal.
export const FLARE_SAFETY: readonly string[] = Object.freeze([
  "Read the instructions on the day — not at midnight in a gale.",
  "Brief the crew on what you're firing and when.",
  "Wear gloves (heat).",
  "Hold over the LEEWARD rail — sparks + embers blow away from you and the boat.",
  "Aim 45° downwind into open water; face turned away.",
  "Parachute rockets: aim ~75° elevation INTO the wind so the parachute drifts downwind over the water.",
  "Keep the boat heading steady — main into wind takes pressure off the rig so it doesn't swing.",
  "Expired flares: do NOT dump at sea. Return to an authorised dealer; UK lifeboat stations + RYA-affiliated clubs run periodic disposal events.",
]);

// ───────────────────────────────────────────────────────────────────
// 2. First-aid kit categories
// ───────────────────────────────────────────────────────────────────

export type FirstAidCategory = "A" | "B" | "C" | "D";

export interface FirstAidKit {
  category: FirstAidCategory;
  /// Voyage scope this kit category is intended for.
  scope: string;
  /// Detailed distance band.
  distance: string;
  /// One-line gist.
  summary: string;
  /// Items emphasised in this tier beyond what a standard kit holds.
  emphasis: readonly string[];
  /// Common-sense contents list.
  contents: readonly string[];
}

export const FIRST_AID_KITS: readonly FirstAidKit[] = Object.freeze([
  {
    category: "D",
    scope: "Short coastal / day-sail",
    distance: "Up to ~4 hours from professional help.",
    summary:
      "The 'standard' first-aid kit plus marine extras. What every yacht has aboard for a day sail in pilotage waters.",
    emphasis: [
      "Assorted adhesive plasters + sterile dressings",
      "Foil emergency blanket (hypothermia)",
      "Triangular bandage + crepe bandage",
      "Alcohol-free cleansing wipes",
      "Nitrile gloves + CPR face shield",
      "Scissors, tweezers, safety pins",
      "Paracetamol + antiseptic cream",
    ],
    contents: [
      "20 assorted adhesive plasters",
      "4 sterile dressings (medium + large)",
      "2 triangular bandages",
      "2 crepe bandages",
      "20 sterile alcohol-free wipes",
      "10 nitrile gloves",
      "1 CPR face shield",
      "1 foil emergency blanket",
      "1 pair scissors + 1 pair tweezers",
      "12 safety pins",
      "8 paracetamol tablets",
      "1 antiseptic cream",
    ],
  },
  {
    category: "C",
    scope: "Coastal — up to 60 nm from safe haven",
    distance: "Voyages where rescue / evac may be hours away.",
    summary:
      "Cat D plus comprehensive trauma + hypothermia + immobilisation kit. The RYA-recommended baseline for any coastal cruising / Day Skipper qualified skipper.",
    emphasis: [
      "Comprehensive hypothermia kit (foil blanket + casualty bag)",
      "Sterile saline (eye + wound irrigation — fresh water rationed at sea)",
      "Splints (large triangular bandages, SAM splint or equivalent)",
      "Cervical collar (recommended)",
      "Burn dressings (10×10 + 20×20)",
      "Seasickness medication (cinnarizine / hyoscine / Stugeron)",
      "Paracetamol + ibuprofen + loperamide (anti-diarrhoea)",
      "Defibrillator (AED) pads if AED carried",
      "Telemedical contact card — Coastguard MRCC, DGREX radio medical advice",
    ],
    contents: [
      "All Cat D contents in increased quantities",
      "Casualty bag (full hypothermia)",
      "SAM splint + extra triangular bandages",
      "Cervical collar (adjustable)",
      "500 ml sterile saline",
      "2 large burn dressings (10×10 cm)",
      "2 extra-large burn dressings (20×20 cm)",
      "Eye irrigation kit + eye pad",
      "Tubular bandages (assorted)",
      "Box of paracetamol (32)",
      "Box of ibuprofen (32)",
      "Box of loperamide (12)",
      "Box of cinnarizine / hyoscine seasickness tabs",
      "Tweezers + scissors + thermometer",
      "AED pads (if AED carried)",
      "Telemedical contact card",
    ],
  },
  {
    category: "B",
    scope: "Offshore — 60–150 nm from safe haven",
    distance: "Voyages where rescue is many hours, possibly a day away.",
    summary:
      "Cat C plus sutures, stronger analgesics, splints for major fractures, urinary catheter, supplementary respiratory kit. Yachtmaster Offshore baseline.",
    emphasis: [
      "Sutures + needle holders + scalpel",
      "Stronger analgesics (codeine / tramadol — prescription required)",
      "Antibiotics — broad-spectrum (prescription required)",
      "Inflatable splints (full-leg, full-arm)",
      "Urinary catheter + collection bag",
      "Pulse oximeter",
      "Bag-valve-mask resuscitator",
    ],
    contents: [
      "All Cat C contents",
      "Sutures (3.0 + 5.0) + needle holders",
      "Disposable scalpel + sterile drape",
      "Codeine 30 mg + tramadol (POM — requires script)",
      "Co-amoxiclav + ciprofloxacin (POM)",
      "Inflatable splints (full-leg + full-arm)",
      "Urinary catheter + bag",
      "Pulse oximeter (finger clip)",
      "Bag-valve-mask resuscitator",
      "Saline IV bag (Hartmann's) + giving set",
    ],
  },
  {
    category: "A",
    scope: "Ocean — over 150 nm from safe haven",
    distance: "Blue-water passages where SAR may be days away or unavailable.",
    summary:
      "Cat B plus IV fluids, dental kit, more antibiotics + analgesics. Yachtmaster Ocean baseline. Requires a designated 'medic' on board with formal training.",
    emphasis: [
      "IV fluid administration (multiple Hartmann's + Dextrose + giving sets)",
      "Comprehensive prescription analgesics + antibiotics",
      "Dental kit (temporary filling + extraction)",
      "Burn-injury kit (silver sulfadiazine, large hydrogel dressings)",
      "Cardiac kit (aspirin + GTN spray + oxygen)",
      "Sterile surgical instruments for minor procedures",
      "Telemedical reference cards (DGREX, IRA, MRCC contact list)",
      "A formally-trained 'medic' on board (RYA SOLAS or equivalent)",
    ],
    contents: [
      "All Cat B contents",
      "Multiple IV fluid bags + administration sets",
      "Dental temporary-filling kit",
      "Comprehensive antibiotics (course quantities)",
      "Stronger prescription analgesics (morphine — controlled drug, register required)",
      "Burn-care kit (large hydrogel dressings, silver dressings)",
      "Cardiac kit (aspirin 300 mg, GTN spray, portable oxygen)",
      "Suction unit (manual + battery)",
      "Telemedical reference cards (Coastguard MRCC, DGREX, IRA)",
    ],
  },
]);

/// What specifically distinguishes Cat C from a standard ("Cat D")
/// kit — pulled out as a Reference talking point because students
/// often think their domestic first-aid box is enough.
export const CAT_C_DIFFERENCES: readonly string[] = Object.freeze([
  "Marine-specific items — hypothermia kit (foil blanket + casualty bag), sterile saline for eye/wound irrigation when fresh water is rationed, seasickness medication.",
  "Higher quantities of basics — bandages, dressings, sterile gauze sized for worst-case (open laceration with continued bleeding for hours before help arrives).",
  "Splinting + immobilisation — large triangular bandages, cervical collar (often included), SAM splint or equivalent.",
  "Defibrillation preparation — AED pads or recommendation to carry an AED if crew or voyage warrants.",
  "Telemedical contact card — UK Coastguard, RCC, DGREX (radio medical advice) reference numbers in a waterproof card.",
]);

// ───────────────────────────────────────────────────────────────────
// 3. Liferaft
// ───────────────────────────────────────────────────────────────────

export type LiferaftType = "coastal" | "offshore" | "ocean";

export interface Liferaft {
  type: LiferaftType;
  /// One-line headline + capacity guidance.
  summary: string;
  /// SOLAS / ISO category this corresponds to.
  category: string;
  /// Service interval (years between professional services).
  serviceIntervalYears: string;
  /// Equipment pack included (SOLAS A / SOLAS B / ISO 9650 sub-types).
  equipmentPack: string;
  /// Operational notes for choosing this type.
  notes: string;
}

export const LIFERAFTS: readonly Liferaft[] = Object.freeze([
  {
    type: "coastal",
    summary:
      "Compact, lighter pack for short coastal passages. Less internal kit, less stability than offshore.",
    category: "ISO 9650 Part 2 / Type 2 Group A",
    serviceIntervalYears: "Every 3 years (some makers extend to 5 with annual inspection)",
    equipmentPack:
      "ISO-9650-2 'Type 2' pack — paddles, drogue, hand bailer, sponges, repair kit, flashlight, first-aid kit (basic).",
    notes:
      "Adequate for inshore + coastal cruising in protected waters. Stowed in a valise or canister; canister is more weather-resistant + has hydrostatic release option.",
  },
  {
    type: "offshore",
    summary:
      "Larger raft with double floor (for insulation) + extra kit. The Yachtmaster Offshore baseline.",
    category: "ISO 9650 Part 1 / Type 1 Group A (single-zone) — SOLAS-style",
    serviceIntervalYears: "Annually (some makers extend to 2 years with documented service)",
    equipmentPack:
      "ISO-9650-1 'A' pack — drogue, paddles, knife, bailer, sponges, repair kit, signalling kit (handheld flares × 6, smoke × 2), first-aid kit (extended), thermal protective aids, water + emergency rations.",
    notes:
      "Mandatory canister + hydrostatic release for SOLAS yachts > 13.7 m. Hydrostatic release fires the raft automatically if the vessel sinks below ~4 m depth.",
  },
  {
    type: "ocean",
    summary:
      "Twin-tube ocean raft, insulated double floor, comprehensive 24-hour-survival pack. Yachtmaster Ocean baseline.",
    category: "ISO 9650 Part 1 'Group A' (extended) / SOLAS A pack",
    serviceIntervalYears: "Annually (mandatory for ocean racing under World Sailing OSR)",
    equipmentPack:
      "Full SOLAS A pack — comprehensive flares (parachute × 4, handheld × 6, smoke × 2), 1.5 l water per person, energy rations (10 000 kJ per person), fishing kit, sea-anchor, signal mirror, full first-aid + survival manual + repair kit.",
    notes:
      "Ocean rafts are heavier (~50 kg per 6-person) — hard to launch single-handed without davit / strop arrangement. Practise launching with the cradle weight before relying on it.",
  },
]);

/// Liferaft launch + boarding sequence — generic SOLAS-style. The
/// numbers vary slightly with raft maker but the order is consistent.
export const LIFERAFT_LAUNCH_STEPS: readonly string[] = Object.freeze([
  "GRAB BAG: send a designated crew below to collect the grab bag (already packed) + EPIRB + handheld VHF + extra water + extra flares.",
  "PAINTER: locate the raft's painter (it's the line tying the raft to the boat). Tie it (or check it's tied) to a STRONG point on the boat — pulpit, mast cleat — NOT a stanchion.",
  "DEPLOY: heave the canister or valise overboard on the LEEWARD side (downwind of the yacht). The drift will carry it away from the hull.",
  "INFLATE: pull the painter sharply until you feel resistance, then a hard tug — the CO2 cylinder fires and the raft inflates in ~30 seconds.",
  "ASSESS: check the raft is the right way up. If inverted, climb onto the upturned floor + use the righting strop on the underside to flip it.",
  "BOARD: get into the raft in this order — strongest first (to help others in), then the injured / cold / weakest, then the skipper LAST. Use the boarding ladder; don't dive in.",
  "TETHERED: stay tethered to the yacht as long as the yacht is afloat — visibility, shelter, height for spotting + collecting things. The raft is the last resort.",
  "CUT: only cut the painter when (a) the yacht is sinking and threatening to drag the raft under, OR (b) staying alongside is more dangerous than drifting.",
  "STREAM the drogue (sea anchor) once clear — keeps the raft bow-on to waves + slows downwind drift.",
  "ACTIVATE: EPIRB on, VHF MAYDAY (handheld), flares ready but not yet fired (save them for when SAR is closing).",
  "SHELTER: close the canopy. Bail water out continuously. Take seasickness tablets. Stay calm + keep crew talking.",
]);

/// Grab-bag contents — minimum recommended kit packed and stowed
/// ready to grab at abandon-ship.
export const GRAB_BAG: readonly string[] = Object.freeze([
  "Handheld VHF (with spare battery, ideally floating + waterproof)",
  "PLB (personal locator beacon) or backup EPIRB",
  "Extra flares — 2 handheld red + 1 smoke",
  "2 litres of bottled water minimum (1 l per person per day target)",
  "High-energy rations (chocolate, glucose gel, biscuits — no thirst-inducers)",
  "First-aid kit (waterproof, Cat C minimum for coastal)",
  "Spare clothing — thermal layers in a drysack",
  "Mobile phone in a waterproof pouch",
  "Whistle (one per person)",
  "Sun cream + sunglasses (sun is fierce at sea level)",
  "Knife (folding, locking, attached to a lanyard)",
  "Passports + copy of insurance documents in a waterproof pouch",
  "Small torch + spare batteries",
  "Sponges + bailer (helps in the raft)",
]);

// ───────────────────────────────────────────────────────────────────
// 4. EPIRB + PLB
// ───────────────────────────────────────────────────────────────────

export type BeaconType = "epirb" | "plb";

export interface Beacon {
  type: BeaconType;
  name: string;
  summary: string;
  /// Activation method.
  activation: string;
  /// Registration requirements.
  registration: string;
  /// Battery + service notes.
  battery: string;
  /// What it transmits.
  transmits: string;
  /// Specific use case.
  bestFor: string;
}

export const BEACONS: readonly Beacon[] = Object.freeze([
  {
    type: "epirb",
    name: "EPIRB — Emergency Position-Indicating Radio Beacon",
    summary:
      "Vessel-registered satellite beacon, normally mounted in a bracket on the vessel. Activates automatically (Category 1 with hydrostatic release) or manually (Category 2).",
    activation:
      "Category 1: hydrostatic release fires at ~4 m depth, the EPIRB floats free + self-activates. Category 2: manual — pull from bracket + activate in the water.",
    registration:
      "Mandatory registration with the national authority (UK: MCA, free). Re-register every 2 years + when the boat changes hands. The registration links the MMSI / hex code to the vessel name + emergency contacts.",
    battery:
      "10-year battery life typical. Self-test every month + at the start of every season. Battery replacement by manufacturer-authorised service centre only.",
    transmits:
      "406 MHz digital distress signal to the Cospas-Sarsat satellite network (vessel ID + GPS position) + 121.5 MHz homing signal for SAR aircraft + 1090 MHz AIS distress signal (modern EPIRBs only).",
    bestFor:
      "Whole-vessel distress — fire, sinking, abandoning ship. Stays with the boat (Cat 1) or the raft (Cat 2). Required for offshore + ocean voyages under most national regulations.",
  },
  {
    type: "plb",
    name: "PLB — Personal Locator Beacon",
    summary:
      "Smaller, person-registered beacon worn on the body or attached to a lifejacket. Designed for MOB + abandon-ship situations.",
    activation:
      "Manual only — slide a cover + press a button + hold above the water for the signal to reach the satellite. Most require deployment of a small antenna.",
    registration:
      "Registered to a PERSON (not a vessel) — the registration links the beacon to the individual + emergency contacts ashore.",
    battery:
      "5-year battery life typical. Self-test monthly. Replace battery via the maker.",
    transmits:
      "406 MHz Cospas-Sarsat signal with the registered ID + GPS position (modern PLBs have integrated GPS) + 121.5 MHz homing.",
    bestFor:
      "Single-person scenarios — MOB recovered to a raft, climber, kayaker. Complement to a vessel EPIRB. Worn at all times by short-handed offshore + ocean sailors.",
  },
]);

// ───────────────────────────────────────────────────────────────────
// 5. Lifejackets
// ───────────────────────────────────────────────────────────────────

export type LifejacketType = "manual" | "automatic" | "hydrostatic" | "inherent";

export interface Lifejacket {
  type: LifejacketType;
  name: string;
  summary: string;
  /// When + how the buoyancy is provided.
  activation: string;
  /// Maintenance intervals and what to check.
  servicing: string;
  /// Best-fit scenario.
  bestFor: string;
}

export const LIFEJACKETS: readonly Lifejacket[] = Object.freeze([
  {
    type: "manual",
    name: "Manual inflation",
    summary:
      "Pull the toggle to fire the CO2 cylinder + inflate the bladder.",
    activation:
      "User decision — pull the toggle. No risk of an accidental inflation, but useless if the wearer is unconscious or panicked.",
    servicing:
      "Annual service: check bladder for leaks + chafe; weigh CO2 cylinder (must be within 5% of stamped mass); check oral inflation tube + valve. Replace bobbins / capsules per maker schedule.",
    bestFor:
      "Trained crew who choose when to inflate (e.g. divers, drysuit users). RARELY recommended for general yacht use.",
  },
  {
    type: "automatic",
    name: "Automatic — water-activated",
    summary:
      "A bobbin dissolves on contact with water; the spring fires the CO2 cylinder automatically.",
    activation:
      "Automatic on immersion — typical activation in 3–5 seconds underwater. Manual pull-toggle as a backup.",
    servicing:
      "Annual service same as manual + REPLACE THE BOBBIN every 2 years (or after activation, or if visibly degraded). Keep dry between trips — spray + rain can shorten bobbin life.",
    bestFor:
      "General yacht crew. Reliable + automatic but bobbins must be in date. UV + spray-prone storage shortens bobbin life.",
  },
  {
    type: "hydrostatic",
    name: "Hydrostatic (UML / Hammar)",
    summary:
      "Sealed capsule that fires only when submerged in water at ~10 cm depth — immune to rain, spray, and condensation.",
    activation:
      "Activates ONLY on actual submersion. No false-positives from rain or spray. Manual pull-toggle as a backup.",
    servicing:
      "Capsule lifespan 5 years (sealed) — replace at expiry regardless of use. Annual bladder + cylinder check. More expensive per service than bobbin types.",
    bestFor:
      "Offshore + ocean sailors, helmsmen who get drenched routinely, racing crews. Doesn't go off in a gale of spray.",
  },
  {
    type: "inherent",
    name: "Inherent buoyancy (foam)",
    summary:
      "Solid-foam jacket — buoyancy always present, no inflation required.",
    activation:
      "Always inflated. No mechanism to fail. Bulkier + less comfortable than gas-inflated.",
    servicing:
      "Annual visual check for tears, UV degradation, stitching integrity. Replace at 10 years.",
    bestFor:
      "Children, non-swimmers, dinghy crews, anyone wearing a buoyancy aid in conditions where automatic inflation is unreliable.",
  },
]);

// ───────────────────────────────────────────────────────────────────
// 6. First aid for sea — cold-water shock, hypothermia, seasickness
// ───────────────────────────────────────────────────────────────────

export interface FirstAidScenario {
  id:
    | "cold-water-shock"
    | "hypothermia"
    | "seasickness"
    | "drowning"
    | "burn-galley"
    | "fracture";
  name: string;
  summary: string;
  /// How to recognise it.
  recognition: string;
  /// Immediate action.
  action: string;
  /// What NOT to do.
  pitfalls: string;
}

export const FIRST_AID_SCENARIOS: readonly FirstAidScenario[] = Object.freeze([
  {
    id: "cold-water-shock",
    name: "Cold-water shock",
    summary:
      "The 1-10-1 rule: 1 minute of gasp reflex, 10 minutes of meaningful movement, 1 hour before hypothermia takes consciousness.",
    recognition:
      "Initial gasp on immersion (60 seconds) — uncontrollable hyperventilation, risk of inhaling water. Then 10 minutes of reducing strength + dexterity. Then progressive hypothermia.",
    action:
      "Float to control the gasp reflex (HELP position: head + chest above water, knees up). Do NOT swim — exhaustion within 10 minutes. Get out of the water as fast as possible. Once recovered: warm dry clothes, sweet warm drinks (NOT alcohol), monitor for hypothermia / circum-rescue collapse.",
    pitfalls:
      "Swimming for the boat instead of floating — exhausts you fast. Removing wet clothes immediately on board — better to insulate over the wet layers, then change in stages. Giving alcohol — drops the core temperature further.",
  },
  {
    id: "hypothermia",
    name: "Hypothermia",
    summary:
      "Core temperature < 35°C. Categories: mild (35–32°), moderate (32–28°), severe (< 28°). The most common cause of death in sailing accidents.",
    recognition:
      "Mild: shivering, slurred speech, clumsy hands ('umbles' — fumbles, mumbles, stumbles). Moderate: shivering stops, confusion, drowsy. Severe: unconscious, very slow pulse, may appear dead but is not — 'no-one is dead until warm and dead'.",
    action:
      "Insulate (foil blanket + casualty bag) — head + neck especially. Move very gently — rough handling triggers cardiac arrhythmia in severe cases. Slow rewarming: warm fluids (sweet, non-alcoholic) only if conscious + swallowing. MAYDAY for evacuation in moderate / severe cases.",
    pitfalls:
      "Rubbing limbs to warm them — drives cold blood to the core (afterdrop), can trigger cardiac arrest. Hot bath / direct heat — fast peripheral rewarming + same afterdrop risk. Giving alcohol — vasodilates, drops core temperature further.",
  },
  {
    id: "seasickness",
    name: "Seasickness",
    summary:
      "Conflict between visual + vestibular input. Affects 50% of sailors at some point; severe cases lead to dehydration + impaired judgement.",
    recognition:
      "Pallor, cold sweat, yawning, salivation. Progresses to nausea, then vomiting. Severe + prolonged: dehydration, exhaustion, hypothermia risk, depression.",
    action:
      "Fresh air, stay on deck, focus on the horizon. Take the helm or a fixed task — distracts the brain + reduces the conflict. Eat dry biscuits. Drink small sips of water. Anti-emetics (cinnarizine / hyoscine / Stugeron) taken BEFORE sailing or at first sign — useless once vomiting. Severe cases: rest on a windward berth, fluids, monitor for dehydration. Severe + persistent: a Pan Pan for medical advice + possible evacuation.",
    pitfalls:
      "Sending the casualty below to lie down — usually makes it worse. Heavy food / fat / alcohol the night before. Continuing without anti-emetics after the first sign — the cycle becomes self-sustaining.",
  },
  {
    id: "drowning",
    name: "Near-drowning / aspiration",
    summary:
      "Casualty has been in the water + has aspirated. Symptoms may delay for hours ('secondary drowning').",
    recognition:
      "Recovered from immersion. May be coughing, wheezing, breathless. Or alert + apparently fine — but watch for delayed onset of breathing difficulty over the next 12–24 hours.",
    action:
      "Check airway, breathing, circulation. If unconscious + not breathing — CPR. If conscious + breathing — recovery position (head down to drain water from airway), oxygen if available, warm + dry. EVERY near-drowning casualty needs medical evaluation ashore — request evacuation.",
    pitfalls:
      "Letting them sleep without observation — they can deteriorate silently. Pressing on the chest / abdomen to 'expel water' — water in the lungs can't be expelled this way + you damage internal organs.",
  },
  {
    id: "burn-galley",
    name: "Galley burn / scald",
    summary:
      "Boiling water + boat motion + open flame = the most common injury at sea. Severity ranges from minor (superficial) to deep + extensive.",
    recognition:
      "Superficial: red, painful, no blisters. Partial-thickness: blisters, very painful. Full-thickness: white / charred, painless (nerves destroyed). Estimate area by Rule of Nines.",
    action:
      "COOL the burn with cool RUNNING water for at least 20 minutes (even if hours later — the cooling helps). Cover with a hydrogel burn dressing or cling-film (NOT cotton wool / sticky dressings). Pain relief: paracetamol + ibuprofen. Maintain warmth — don't let the casualty cool overall while you cool the burn locally.",
    pitfalls:
      "Ice or icy water — causes additional cold injury. Butter / toothpaste / aloe vera — old wives' remedies, no benefit + complicate clinical treatment. Removing clothing stuck to burn — leave it, let medics deal with it.",
  },
  {
    id: "fracture",
    name: "Fracture / suspected fracture",
    summary:
      "Sailing fractures: head + thorax (boom), ribs (falls), wrist + forearm (catching a fall). Diagnose by mechanism + symptoms; X-ray is impossible at sea.",
    recognition:
      "Pain on movement + tenderness on palpation; deformity; swelling; loss of function; crepitus (grinding) if you palpate carefully. SAM splint or improvised splint stabilises.",
    action:
      "Immobilise in the position found — DON'T attempt to 'set' a displaced fracture without training. SAM splint, triangular bandages, or improvised splint with foam + cardboard. Check circulation distal to the fracture (pulse, capillary refill, sensation, movement) before + after splinting. Pain relief: paracetamol + ibuprofen + codeine (Cat B+ kit only). Pan Pan for medical advice — evacuation usually warranted for limb fractures.",
    pitfalls:
      "Reducing fractures yourself — risks neurovascular damage. Tight splinting — can compress the limb + cause distal ischaemia. Not checking distal circulation after splinting.",
  },
]);

// ───────────────────────────────────────────────────────────────────
// 7. Capsize drill (dinghy + keel-boat) — extended detail
// ───────────────────────────────────────────────────────────────────

export type CapsizeKind =
  | "dinghy-side"
  | "dinghy-inverted"
  | "dinghy-dry"
  | "yacht-knockdown";

export interface CapsizeScenario {
  id: CapsizeKind;
  name: string;
  summary: string;
  /// Specific recovery sequence for this kind of capsize.
  steps: readonly string[];
  /// Critical mistakes to avoid.
  pitfalls: readonly string[];
}

export const CAPSIZE_SCENARIOS: readonly CapsizeScenario[] = Object.freeze([
  {
    id: "dinghy-side",
    name: "Dinghy capsize — side (mast in the water)",
    summary:
      "The standard dinghy capsize. Boat is on its side, sails + mast horizontal in the water, both crew in the water.",
    steps: [
      "STAY WITH THE BOAT — it floats, you can hold on, rescuers see it.",
      "Check buddy: each crew calls + confirms the other is OK + above water.",
      "Crew climbs onto the centreboard / daggerboard. NEVER pull on the gunwale — pushes the rig deeper, risks inversion.",
      "Helm holds the toe-strap or main thwart, stays in the water near the cockpit.",
      "Crew leans back on the board, keeping weight close to the hull. Boat starts to come up.",
      "As the boat rights, the helm is SCOOPED into the cockpit by the rising hull — no need to swim aboard.",
      "Crew climbs in over the windward side; sheets released so the boat doesn't immediately re-capsize.",
      "Sort sheets, bail out cockpit, settle, sail away.",
    ],
    pitfalls: [
      "Pulling on the gunwale — pushes the rig deeper + risks a full inversion.",
      "Letting helm + crew separate from the boat — boat drifts downwind faster than you can swim.",
      "Not releasing sheets before righting — boat re-fills with wind + capsizes again.",
      "Sailing away with the daggerboard half up — unbalanced + weather helm.",
    ],
  },
  {
    id: "dinghy-inverted",
    name: "Dinghy capsize — fully inverted (turtle)",
    summary:
      "Boat has gone fully upside down. Mast pointing straight down, hull skywards. Harder to recover than a side-capsize but the same principles apply.",
    steps: [
      "STAY WITH THE BOAT. Each crew accounts for the other.",
      "Both crew swim to the bow (UPWIND end of the hull).",
      "Two crew + the righting strop (or trapeze wires) on the centreboard — lean back together. The mass of two does the work that one couldn't.",
      "Boat comes up to the horizontal side-capsize position. Now apply the standard side-recovery — one on the board, one in the water near the cockpit.",
      "Once righted, get in fast — these recoveries can take 5+ minutes and you're getting cold.",
      "Sheets released; settle on a tack; bail; head for sheltered water.",
    ],
    pitfalls: [
      "Approaching from the wrong end — the rig stops you righting from the stern.",
      "Trying alone — needs two people for the moment-of-inertia in most rigs.",
      "Hypothermia ignored — after 10 minutes in cold water, judgement is impaired. Get out quickly.",
    ],
  },
  {
    id: "dinghy-dry",
    name: "Dinghy capsize — dry (scoop)",
    summary:
      "The skilled version: only the helm gets wet, the crew stays dry. Possible with practice + the right boat.",
    steps: [
      "As the boat starts to go over, the helm steps over the high side + into the cockpit on the way down, then steps OUT of the cockpit + onto the centreboard as the boat reaches horizontal.",
      "Helm is now standing on the centreboard with the crew dry on the upturned hull.",
      "Both lean their weight on the board; boat rights.",
      "Helm is SCOOPED into the cockpit; crew steps off the hull into the cockpit.",
      "Sheets released, settle, sail away — dry.",
    ],
    pitfalls: [
      "Mistiming the step — falling in instead of staying on top.",
      "Trying it in heavy weather without practice in light — practice in benign conditions before relying on it.",
    ],
  },
  {
    id: "yacht-knockdown",
    name: "Yacht knockdown (90° heel + masts in water)",
    summary:
      "Yacht laid flat by a gust or breaking sea. Self-rights if ballasted properly — but very disorienting + dangerous if anyone is below.",
    steps: [
      "Helm: BEAR AWAY hard + EASE the sheets — depower so the boat can right.",
      "Crew on deck: stay LOW + clipped on. Anyone unclipped is going overboard.",
      "Crew below: brace + protect head. Loose objects + crockery now flying.",
      "Boat rights — the ballasted keel pulls it back upright in seconds.",
      "Account for all crew — head count, check for injuries.",
      "Reduce sail (reef the main, change to smaller headsail). The conditions that caused the knockdown will likely cause another.",
      "If water has come below — pump bilges immediately, locate the source.",
    ],
    pitfalls: [
      "Sheets NOT eased — keeps the rig pinned down + delays righting.",
      "Continuing to sail at the same press of canvas — invites the next knockdown.",
      "Anyone unclipped going forward — knockdown is the worst case for going overboard.",
      "Not pumping bilges promptly if water came below — dangerous flooding spreads fast.",
    ],
  },
]);
